/**
 * Print-Agent Dashboard - Moderne, elegante Seite
 * Design: Teal/Emerald Akzente, Glassmorphism, Premium-Look
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, Printer, CheckCircle, XCircle, Clock, Zap, 
  Smartphone, Wifi, HardDrive, ArrowRight, ExternalLink, Copy,
  CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PrintAgentDashboard() {
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const AGENT_TOKEN = 'cxp_f0a0de710bdb4eb58081cfc9';

  useEffect(() => {
    loadAgentStatus();
    const interval = setInterval(loadAgentStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadAgentStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/cloud-print/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgentStatus(data);
      }
    } catch (error) {
      console.error('Status-Fehler:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (version = 'debug') => {
    try {
      setDownloading(true);
      
      const backendUrl = API_BASE || window.location.origin;
      const downloadUrl = version === 'debug' 
        ? `${backendUrl}/api/agent/download/v3.1-debug`
        : `${backendUrl}/api/agent/download/simple-working`;
      
      const response = await fetch(downloadUrl);
      
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = version === 'debug' 
        ? 'CashX-Print-Agent-v3.1-DEBUG.zip'
        : 'CashX-Print-Agent-v3.zip';
      link.click();
      
      window.URL.revokeObjectURL(url);
      
      toast.success('Download gestartet!', {
        description: version === 'debug' 
          ? '⚠️ Debug-Version mit ausführlichen Logs'
          : 'Prüfen Sie Ihren Downloads-Ordner'
      });
      
    } catch (error) {
      toast.error('Download-Fehler', {
        description: error.message
      });
    } finally {
      setDownloading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(AGENT_TOKEN);
    setCopied(true);
    toast.success('Token kopiert!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center border border-teal-500/30">
              <Printer className="w-7 h-7 text-teal-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Print-Agent
              </h1>
              <p className="text-slate-400 text-lg">
                Drucken Sie von überall - automatisch im Restaurant
              </p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        {agentStatus && (
          <Card className="bg-[#111C32]/80 backdrop-blur border-slate-700/50 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    agentStatus.agent_connected 
                      ? 'bg-emerald-500/20 border border-emerald-500/40' 
                      : 'bg-amber-500/20 border border-amber-500/40'
                  }`}>
                    {agentStatus.agent_connected ? (
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <Clock className="w-6 h-6 text-amber-400" />
                    )}
                  </div>
                  <div>
                    <div className="text-white font-semibold text-lg mb-1">
                      {agentStatus.agent_connected ? 'Agent verbunden' : 'Agent offline'}
                    </div>
                    <div className="text-sm text-slate-400">
                      {agentStatus.agent_connected 
                        ? 'Bereit zum Drucken von überall' 
                        : 'Installieren Sie den Agent auf Ihrem Haupt-PC'}
                    </div>
                  </div>
                </div>
                
                <Badge className={
                  agentStatus.agent_connected 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 px-4 py-2 text-sm' 
                    : 'bg-amber-500/20 text-amber-400 border-amber-500/50 px-4 py-2 text-sm'
                }>
                  {agentStatus.agent_connected ? '● Online' : '○ Offline'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* Download Card */}
          <Card className="bg-[#111C32] border-slate-700/50 hover:border-teal-500/30 transition-all">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Download className="w-5 h-5 text-teal-400" />
                </div>
                <CardTitle className="text-white">Print-Agent herunterladen</CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                Installieren Sie den Agent auf Ihrem Haupt-PC im Restaurant
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Warnung Box - NEU! */}
              <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-amber-400 font-semibold mb-1">⚡ NEU: Debug-Version v3.1</div>
                    <div className="text-sm text-slate-300">
                      Wenn der Druck nicht funktioniert, laden Sie die Debug-Version herunter. 
                      Sie zeigt <span className="text-amber-400 font-semibold">ausführliche Logs</span> zur Fehlersuche.
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl border border-teal-500/30">
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCheck className="w-4 h-4 text-teal-400" />
                    <span>Cloud-kompatibel (HTTP-Polling)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCheck className="w-4 h-4 text-teal-400" />
                    <span>ESC/POS für Thermodrucker</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCheck className="w-4 h-4 text-teal-400" />
                    <span>Automatische Konfiguration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCheck className="w-4 h-4 text-teal-400" />
                    <span>Größe: 53 KB</span>
                  </div>
                </div>
              </div>

              {/* Zwei Download-Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => handleDownload('debug')}
                  disabled={downloading}
                  className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-lg font-semibold shadow-lg shadow-amber-500/20"
                  data-testid="download-debug-agent-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  {downloading ? 'Lädt...' : '🐛 v3.1 DEBUG (Empfohlen bei Problemen)'}
                </Button>

                <Button
                  onClick={() => handleDownload('stable')}
                  disabled={downloading}
                  variant="outline"
                  className="w-full h-12 bg-[#0B1220] border-teal-500/30 hover:bg-teal-500/10 text-teal-400 text-base"
                  data-testid="download-stable-agent-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloading ? 'Lädt...' : 'v3.0 Standard-Version'}
                </Button>
              </div>

              <div className="text-center pt-2">
                <div className="text-xs text-slate-500 mb-2">Direkte Download-Links:</div>
                <div className="flex flex-col gap-1">
                  <a
                    href={`${API_BASE || window.location.origin}/api/agent/download/v3.1-debug`}
                    className="text-xs text-amber-400 hover:text-amber-300 underline inline-flex items-center justify-center gap-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-3 h-3" />
                    v3.1 Debug
                  </a>
                  <a
                    href={`${API_BASE || window.location.origin}/api/agent/download/simple-working`}
                    className="text-xs text-teal-400 hover:text-teal-300 underline inline-flex items-center justify-center gap-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-3 h-3" />
                    v3.0 Standard
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Token Card */}
          <Card className="bg-[#111C32] border-slate-700/50">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <CardTitle className="text-white">Agent-Token</CardTitle>
              </div>
              <CardDescription className="text-slate-400">
                Dieser Token verbindet Ihren Agent mit dem Backend
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <div className="font-mono text-sm text-slate-300 mb-3 break-all">
                  {AGENT_TOKEN}
                </div>
                <Button
                  onClick={copyToken}
                  variant="outline"
                  size="sm"
                  className="w-full border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                  data-testid="copy-token-btn"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Kopiert!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Token kopieren
                    </>
                  )}
                </Button>
              </div>

              <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <p className="text-xs text-blue-300">
                  💡 Der Token ist bereits in der heruntergeladenen ZIP vorkonfiguriert!
                </p>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Anleitung */}
        <Card className="bg-[#111C32] border-slate-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-teal-400" />
              Installation (1 Minute)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '1',
                  title: 'ZIP entpacken',
                  description: 'Rechtsklick → "Alle extrahieren"',
                  icon: Download
                },
                {
                  step: '2',
                  title: 'START.bat ausführen',
                  description: 'Doppelklick - erstellt config.json automatisch',
                  icon: Zap
                },
                {
                  step: '3',
                  title: 'Fertig!',
                  description: 'Agent läuft und wartet auf Druckaufträge',
                  icon: CheckCircle
                }
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="p-5 bg-slate-800/30 rounded-xl border border-slate-700/50 hover:border-teal-500/30 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold">
                        {item.step}
                      </div>
                      <Icon className="w-5 h-5 text-teal-400" />
                    </div>
                    <div className="font-semibold text-white mb-1">{item.title}</div>
                    <div className="text-sm text-slate-400">{item.description}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* Remote-Bestellungen */}
          <Card className="bg-[#111C32] border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-400" />
                Von überall bestellen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <ArrowRight className="w-3 h-3 text-teal-400" />
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium mb-1">Unterwegs (Handy)</div>
                    <div className="text-xs text-slate-400">Bestellung über Browser aufnehmen</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <ArrowRight className="w-3 h-3 text-teal-400" />
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium mb-1">Cloud-Sync</div>
                    <div className="text-xs text-slate-400">Bestellung landet in der Cloud</div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium mb-1">Automatischer Druck</div>
                    <div className="text-xs text-slate-400">Bon kommt im Restaurant raus (max. 3 Sek)</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <div className="text-xs text-emerald-300 font-medium">
                  Genau wie bei Toast, Square & Lightspeed!
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System-Anforderungen */}
          <Card className="bg-[#111C32] border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-400" />
                System-Anforderungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: 'Betriebssystem', value: 'Windows 10/11', icon: CheckCircle },
                  { label: 'Arbeitsspeicher', value: 'Mind. 2 GB RAM', icon: CheckCircle },
                  { label: 'Node.js', value: 'v18+ (nodejs.org)', icon: ExternalLink },
                  { label: 'Internetverbindung', value: 'Für Cloud-Sync', icon: Wifi }
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-300">{item.label}</span>
                      </div>
                      <span className="text-sm text-white font-medium">{item.value}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Anleitung */}
        <Card className="bg-[#111C32] border-slate-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-teal-400" />
              Installation (1 Minute)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '1',
                  title: 'ZIP entpacken',
                  description: 'Rechtsklick → "Alle extrahieren"',
                  icon: Download
                },
                {
                  step: '2',
                  title: 'START.bat ausführen',
                  description: 'Doppelklick - erstellt config.json automatisch',
                  icon: Zap
                },
                {
                  step: '3',
                  title: 'Fertig!',
                  description: 'Agent läuft und wartet auf Druckaufträge',
                  icon: CheckCircle
                }
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="p-5 bg-slate-800/30 rounded-xl border border-slate-700/50 hover:border-teal-500/30 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-500 text-white flex items-center justify-center font-bold">
                        {item.step}
                      </div>
                      <Icon className="w-5 h-5 text-teal-400" />
                    </div>
                    <div className="font-semibold text-white mb-1">{item.title}</div>
                    <div className="text-sm text-slate-400">{item.description}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Support */}
        <Card className="bg-[#111C32] border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white">Bei Problemen?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="text-sm text-white font-semibold mb-2">"Jobs werden nicht gedruckt"</div>
                <div className="text-xs text-slate-400">
                  Prüfen Sie Drucker-Namen in config.json - müssen exakt wie in Windows sein
                </div>
              </div>
              
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="text-sm text-white font-semibold mb-2">"Keine Drucker gefunden"</div>
                <div className="text-xs text-slate-400">
                  Installieren Sie Drucker in Windows → Einstellungen → Drucker & Scanner
                </div>
              </div>
              
              <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                <div className="text-sm text-white font-semibold mb-2">"Agent startet nicht"</div>
                <div className="text-xs text-slate-400">
                  Installieren Sie Node.js von nodejs.org (LTS-Version)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
